/**
 * Define Targets dialog — bulk proposed-target capture for one WBS stage.
 *
 * v3_32: targets are a START/END window per floor (or per building for
 * building-wide stages). Target End maps to the existing plannedDate field;
 * Target Start is new. At least one of the two is required per filled row —
 * both dates are entered manually (no auto-duration).
 *
 * The bulk-plan endpoint upserts target dates only — achieved dates and
 * progress on existing rows are never touched, so re-planning is always safe.
 * A re-plan that sends one side never clears the other.
 */
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../Buttons";
import { isFloorScoped } from "../../constants/wbsStages";
import { bulkPlanConstructionEvents } from "../../services/wbs/index";

function DefineTargetsDialog({ stage, buildingId, buildingName, numberOfFloors, events, onClose, onSaved }) {
  const floorScoped = isFloorScoped(stage);

  // Prefill from existing events for this stage (dates are "YYYY-MM-DD").
  const initial = useMemo(() => {
    const rows = {};
    (events || [])
      .filter((ev) => ev.wbsStage?.wbsStageId === stage.wbsStageId)
      .forEach((ev) => {
        const key = ev.floorNumber !== null && ev.floorNumber !== undefined ? ev.floorNumber : "building";
        rows[key] = { start: ev.targetStartDate || null, end: ev.plannedDate || null };
      });
    return rows;
  }, [events, stage]);

  const [rows, setRows] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);

  const floors = Array.from({ length: (numberOfFloors || 0) + 1 }, (_, i) => i);

  const setDate = (key, side, value) => {
    setRows((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [side]: value || null } }));
  };

  const handleSave = async () => {
    const body = { buildingId, wbsStageId: stage.wbsStageId };
    const badOrder = (r) => r?.start && r?.end && r.end < r.start;

    if (floorScoped) {
      const items = floors
        .filter((floor) => rows[floor]?.start || rows[floor]?.end)
        .map((floor) => ({
          floor,
          targetStartDate: rows[floor]?.start || null,
          plannedDate: rows[floor]?.end || null,
        }));
      if (items.length === 0) {
        return toast.error("Set a target for at least one floor.");
      }
      const broken = floors.find((f) => badOrder(rows[f]));
      if (broken !== undefined) {
        return toast.error(`Floor ${broken}: Target End cannot be before Target Start.`);
      }
      body.floors = items;
    } else {
      const r = rows.building || {};
      if (!r.start && !r.end) return toast.error("Set at least one target date.");
      if (badOrder(r)) return toast.error("Target End cannot be before Target Start.");
      body.targetStartDate = r.start || null;
      body.plannedDate = r.end || null;
    }

    setIsSaving(true);
    try {
      await bulkPlanConstructionEvents(body);
      toast.success("Target dates saved");
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error("Error saving target dates:", error);
      toast.error(error?.response?.data?.detail || "Error saving target dates.");
    } finally {
      setIsSaving(false);
    }
  };

  const picker = (key, side) => (
    <input
      type="date"
      value={rows[key]?.[side] || ""}
      onChange={(e) => setDate(key, side, e.target.value)}
      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    />
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative bg-white rounded-lg shadow-xl max-h-[85vh] w-[560px] max-w-[95vw] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Define Targets — {stage.label}</h3>
            <p className="text-sm text-gray-500">
              {buildingName}
              {floorScoped
                ? " · target start & end per floor (either may be left open)"
                : " · one target window for the building"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {floorScoped ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="py-2 pr-3 w-24">Floor</th>
                  <th className="py-2 pr-2">Target Start</th>
                  <th className="py-2">Target End</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {floors.map((floor) => (
                  <tr key={floor}>
                    <td className="py-2 pr-3 font-medium text-gray-700 whitespace-nowrap">
                      {floor === 0 ? "Ground" : `Floor ${floor}`}
                    </td>
                    <td className="py-2 pr-2">{picker(floor, "start")}</td>
                    <td className="py-2">{picker(floor, "end")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Target Start</label>
                {picker("building", "start")}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Target End</label>
                {picker("building", "end")}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
          <OutlineButton className="px-4 py-2 text-sm" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton className="px-4 py-2 text-sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save Targets"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default DefineTargetsDialog;
